import { useDisplay } from "vuetify/lib/composables/display.mjs";

export const useAppBar = () => {
  //
  // Composables
  //

  // Route
  const route = useRoute();

  // Breakpoints
  const { smAndDown } = useDisplay();

  // Localization
  const { t } = useI18n();

  //
  // State
  //
  const currentPath = computed(() => route.path);

  /**
   * Custom page title — set by detail-page composables to show breadcrumb-style
   * titles like "All Visits / Иван Петров М, 77". When non-empty, this takes
   * precedence over the route-based default title.
   */
  const customPageTitle = useState<string>("ui.customPageTitle", () => "");

  //
  // Computed Properties
  //

  /** Route-based default page title resolved from i18n keys */
  const defaultPageTitle = computed(() => {
    // Get the path without locale prefix
    const pathWithoutLocale = route.path.replace(/^\/(en|bg|mk)/, "") || "/";

    // Exact route → translation key mapping
    const titleMap: Record<string, string> = {
      "/": "components.common.app-bar.page_titles.login",
      "/app/validation": "components.common.app-bar.page_titles.validation",
      "/app/about": "components.common.app-bar.page_titles.about",
      "/app/corporate/all-visits": "pages.all-visits.title",
      "/app/corporate/new-results": "pages.new-results.title",
      "/app/corporate/search": "pages.search.title",
      "/app/corporate/starred": "pages.starred.title",
      "/app/dev/ui-states": "pages.dev.uiStates.title",
    };

    // Prefix routes for dynamic segments (e.g. /app/otp-patient/results/:id)
    const prefixMap: Array<[string, string]> = [
      ["/app/otp-patient/results/", "pages.otp-patient.results.title"],
      ["/app/otp-patient/balance/", "pages.otp-patient.balance.title"],
      ["/app/corporate/record/", "pages.all-visits.title"],
      ["/app/corporate/results/", "pages.all-visits.title"],
    ];

    if (titleMap[pathWithoutLocale]) {
      return t(titleMap[pathWithoutLocale]);
    }

    for (const [prefix, key] of prefixMap) {
      if (pathWithoutLocale.startsWith(prefix)) {
        return t(key);
      }
    }

    // Generic fallback — shows lab name only via layout head watcher
    return "";
  });

  /** Effective page title: custom breadcrumb title if set, else route-based default */
  const pageTitle = computed(() => customPageTitle.value || defaultPageTitle.value);

  /**
   * Get app bar extension configuration for current route
   */
  const getExtensionConfig = computed(() => {
    const path = currentPath.value;
    // Strip locale prefix so comparisons work regardless of active locale
    const pathWithoutLocale = path.replace(/^\/(en|bg|mk)/, "") || "/";

    // Exact matches only
    const exactConfigs: Record<string, { show: boolean; component: string }> = {
      "/app": { show: false, component: "ChannelsExtension" },
      "/app/validation": { show: smAndDown.value, component: "ValidationExtension" },
      "/app/about": { show: true, component: "AboutExtension" },
      "/app/corporate/all-visits": { show: false, component: "AllVisitsActions" },
      "/app/dev/ui-states": { show: true, component: "DevStatesExtension" },
    };

    // Partial matches (for nested routes with dynamic segments)
    const partialConfigs: Array<[string, { show: boolean; component: string }]> = [
      ["/app/otp-patient/results", { show: false, component: "OtpResultsActions" }],
      ["/app/otp-patient/balance", { show: false, component: "OtpBalanceActions" }],
      ["/app/corporate/record", { show: false, component: "PatientRecordActions" }],
      ["/app/corporate/results", { show: false, component: "CorporateResultsActions" }],
    ];

    // Check exact match first
    if (exactConfigs[pathWithoutLocale]) {
      return exactConfigs[pathWithoutLocale];
    }

    // Check partial matches
    for (const [prefix, config] of partialConfigs) {
      if (pathWithoutLocale.startsWith(prefix)) {
        return config;
      }
    }

    // Default: no extension
    return { show: false, component: "" };
  });

  //
  // Methods
  //

  /**
   * Set a custom page title (e.g. breadcrumb: "All Visits / Patient Name").
   * Takes precedence over the route-based default title.
   */
  const setPageTitle = (title: string): void => {
    customPageTitle.value = title;
  };

  /**
   * Reset the custom page title so the route-based default title is used.
   * Must be called in onBeforeUnmount of any page that sets a custom title.
   */
  const resetPageTitle = (): void => {
    customPageTitle.value = "";
  };

  return {
    pageTitle: readonly(pageTitle),
    extensionsConfig: getExtensionConfig,
    setPageTitle,
    resetPageTitle,
  } as const;
};
